import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useJobFamilyApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// İş Aileleri (spec 2026-10-27 §7.1) — TitlesPage aynası.
export default function JobFamiliesPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useJobFamilyApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow ? await update(selectedRow.id, formData) : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: t('jobFamily.selectedCount', { count: ids.length }) });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'edit', label: t('jobFamily.edit'), onClick: handleEdit },
      { type: 'divider' },
      { type: 'delete', label: t('jobFamily.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleEdit, handleDelete]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => <OptionsMenu actions={actionOptions} row={data[tableMeta.rowIndex]} />,
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('jobFamily.edit') : t('jobFamily.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('jobFamily.deleteTitle')}
        message={t('jobFamily.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      <DataTable
        title={t('jobFamily.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={canWrite('EM')}
        onNewButtonClick={handleNewButtonClick}
        onRowsDelete={canWrite('EM') ? handleRowsDelete : undefined}
        filterList={filterList}
        onFilterConfirm={setFilterList}
        onTableChanged={handleDataStateChange}
      />
    </Container>
  );
}
